import { useState, useRef, useCallback, useEffect, useMemo } from 'react';

const TOUCH_LONG_PRESS_MS = 400;
const TOUCH_MOVE_CANCEL_PX = 10;
const MOUSE_DRAG_START_PX = 6;
const VIBRATE_MS = 25;

/**
 * [F구역 ⑧-4] 단체 화상 격자 칸 길게 눌러 끌어서 맞바꾸기 훅
 * - 모바일(터치): 400ms 누름(10px 이내) 시 25ms 햅틱 후 드래그
 * - 데스크톱(마우스): 누른 채 6px 이상 이동 시 즉시 드래그
 * - DOM 노드 이동 없이 CSS order 및 col-span-2 슬롯 교체로 영상 깜빡임 0
 */
export function useGroupGridSwap({ tileIds = [], enabled = true, containerRef = null, onDragStart = null, onSwap = null }) {
  const [slotOrder, setSlotOrder] = useState([]);
  const [draggingId, setDraggingId] = useState(null);
  const [dropTargetId, setDropTargetId] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  const effectiveOrder = useMemo(() => {
    const existing = slotOrder.filter((id) => tileIds.includes(id));
    const added = tileIds.filter((id) => !existing.includes(id));
    return [...existing, ...added];
  }, [slotOrder, tileIds]);

  const getSlotIndex = useCallback((id) => Math.max(0, effectiveOrder.indexOf(id)), [effectiveOrder]);
  const resetOrder = useCallback(() => setSlotOrder([]), []);

  const stateRef = useRef({ isDown: false, isDragging: false, id: null, pointerId: null, pointerType: '', startX: 0, startY: 0, currentDx: 0, currentDy: 0, el: null });
  const longPressTimerRef = useRef(null);
  const tileRectsRef = useRef({});
  const rafIdRef = useRef(null);

  const isDragActive = useCallback(() => stateRef.current.isDragging === true, []);

  const cancelLongPress = useCallback(() => {
    if (longPressTimerRef.current) { clearTimeout(longPressTimerRef.current); longPressTimerRef.current = null; }
  }, []);

  const measureTileRects = useCallback(() => {
    const container = containerRef?.current;
    if (!container) return;
    const rects = {};
    container.querySelectorAll('[data-tile-id]').forEach((el) => {
      const tid = el.getAttribute('data-tile-id');
      if (tid) rects[tid] = el.getBoundingClientRect();
    });
    tileRectsRef.current = rects;
  }, [containerRef]);

  const findDropTarget = useCallback((clientX, clientY, sourceId) => {
    const rects = tileRectsRef.current;
    if (!rects) return null;
    for (const tid of Object.keys(rects)) {
      if (tid === sourceId) continue;
      const r = rects[tid];
      if (r && clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom) return tid;
    }
    return null;
  }, []);

  const startDrag = useCallback((id, el, pointerId) => {
    if (!enabled || tileIds.length < 2) return;
    stateRef.current.isDragging = true;
    stateRef.current.id = id;
    stateRef.current.el = el;
    stateRef.current.pointerId = pointerId;
    try { if (pointerId != null && el?.setPointerCapture) el.setPointerCapture(pointerId); } catch {}
    measureTileRects();
    setDraggingId(id);
    setDropTargetId(null);
    setDragOffset({ x: 0, y: 0 });
    onDragStart?.(id);
  }, [enabled, tileIds.length, measureTileRects, onDragStart]);

  const finishDrag = useCallback((isCancel = false) => {
    cancelLongPress();
    if (rafIdRef.current) { cancelAnimationFrame(rafIdRef.current); rafIdRef.current = null; }
    const { isDragging, id, el, pointerId } = stateRef.current;
    try { if (pointerId != null && el?.hasPointerCapture?.(pointerId)) el.releasePointerCapture(pointerId); } catch {}
    stateRef.current = { isDown: false, isDragging: false, id: null, pointerId: null, pointerType: '', startX: 0, startY: 0, currentDx: 0, currentDy: 0, el: null };

    if (!isDragging) {
      setDraggingId(null);
      setDropTargetId(null);
      setDragOffset({ x: 0, y: 0 });
      return;
    }

    if (!isCancel && id && dropTargetId && dropTargetId !== id) {
      setSlotOrder(() => {
        const current = effectiveOrder.slice();
        const sIdx = current.indexOf(id);
        const tIdx = current.indexOf(dropTargetId);
        if (sIdx >= 0 && tIdx >= 0) {
          current[sIdx] = dropTargetId;
          current[tIdx] = id;
          console.log(`[단체 화상] 격자 칸 맞바꿈 — ${id.slice(0, 8)}(${sIdx}) ↔ ${dropTargetId.slice(0, 8)}(${tIdx})`);
          return current;
        }
        return current;
      });
      navigator.vibrate?.(15);
      onSwap?.(id, dropTargetId);
    }
    setDraggingId(null);
    setDropTargetId(null);
    setDragOffset({ x: 0, y: 0 });
  }, [cancelLongPress, dropTargetId, effectiveOrder, onSwap]);

  const cancelDrag = useCallback(() => finishDrag(true), [finishDrag]);

  const tileCount = tileIds.length;
  useEffect(() => {
    if (!enabled || tileCount < 2 || (draggingId && !tileIds.includes(draggingId))) cancelDrag();
    if (dropTargetId && !tileIds.includes(dropTargetId)) setDropTargetId(null);
  }, [enabled, tileCount, tileIds, draggingId, dropTargetId, cancelDrag]);

  useEffect(() => {
    const handleVis = () => { if (document.visibilityState === 'hidden') cancelDrag(); };
    const handleBlur = () => cancelDrag();
    document.addEventListener('visibilitychange', handleVis);
    window.addEventListener('blur', handleBlur);
    return () => {
      document.removeEventListener('visibilitychange', handleVis);
      window.removeEventListener('blur', handleBlur);
    };
  }, [cancelDrag]);

  const getSwapHandlers = useCallback((id) => ({
    onPointerDown: (e) => {
      if (!enabled || tileIds.length < 2 || !e.isPrimary || e.target.closest('button')) return;
      cancelLongPress();
      const el = e.currentTarget;
      const pointerType = e.pointerType || 'mouse';
      stateRef.current = { isDown: true, isDragging: false, id, pointerId: e.pointerId, pointerType, startX: e.clientX, startY: e.clientY, currentDx: 0, currentDy: 0, el };
      if (pointerType === 'touch' || pointerType === 'pen') {
        longPressTimerRef.current = setTimeout(() => {
          longPressTimerRef.current = null;
          if (stateRef.current.isDown && !stateRef.current.isDragging) {
            navigator.vibrate?.(VIBRATE_MS);
            startDrag(id, el, e.pointerId);
          }
        }, TOUCH_LONG_PRESS_MS);
      }
    },
    onPointerMove: (e) => {
      if (!stateRef.current.isDown) return;
      const dx = e.clientX - stateRef.current.startX;
      const dy = e.clientY - stateRef.current.startY;
      const dist = Math.hypot(dx, dy);
      if (!stateRef.current.isDragging) {
        const isTouch = stateRef.current.pointerType === 'touch' || stateRef.current.pointerType === 'pen';
        if (isTouch) {
          if (dist > TOUCH_MOVE_CANCEL_PX) cancelLongPress();
          return;
        }
        if (e.button === 0 && dist >= MOUSE_DRAG_START_PX) startDrag(id, stateRef.current.el, e.pointerId);
        else return;
      }
      stateRef.current.currentDx = dx;
      stateRef.current.currentDy = dy;
      setDropTargetId(findDropTarget(e.clientX, e.clientY, id));
      if (!rafIdRef.current) {
        rafIdRef.current = requestAnimationFrame(() => {
          rafIdRef.current = null;
          if (stateRef.current.isDragging) setDragOffset({ x: stateRef.current.currentDx, y: stateRef.current.currentDy });
        });
      }
    },
    onPointerUp: () => finishDrag(false),
    onPointerCancel: () => finishDrag(true),
    onPointerLeave: () => { if (!stateRef.current.isDragging) cancelLongPress(); },
    onContextMenu: (e) => e.preventDefault(),
    onDragStart: (e) => e.preventDefault(),
  }), [enabled, tileIds.length, cancelLongPress, startDrag, findDropTarget, finishDrag]);

  return { getSlotIndex, getSwapHandlers, draggingId, dropTargetId, dragOffset, isDragActive, cancelLongPress, resetOrder };
}
